/* tc-kit.js -- shared behavior for the tc- homepage section kit.
   Loaded once per page via snippets/tc-kit-assets.liquid. The snippet is
   rendered by every kit section, so this file can be requested by several
   script tags; the guard below makes the second execution a no-op.
   Spec: docs/specs/2026-08-06-homepage-native-rebuild-design.md section 5.
   ES5 syntax only: the theme still ships an ie11 polyfill path. */
(function () {
  'use strict';
  if (window.TCKit) { return; }

  var registry = {};
  var live = [];
  var booted = false;

  function each(list, fn) { Array.prototype.forEach.call(list, fn); }

  function rootsIn(scope, type) {
    var sel = type ? '[data-tc-section="' + type + '"]' : '[data-tc-section]';
    var found = [];
    if (scope && scope.nodeType === 1 && scope.matches && scope.matches(sel)) { found.push(scope); }
    if (scope && scope.querySelectorAll) { each(scope.querySelectorAll(sel), function (el) { found.push(el); }); }
    return found;
  }

  var TCKit = {
    /* type: String, must equal the section filename without the tc- prefix.
       handlers: { init: fn(root), destroy: fn(root), blockSelect: fn(root, blockEl) }
       destroy and blockSelect are optional.
       Registering after the initial boot re-runs init, so a module appended to
       the bottom of this file still picks up the roots already in the document.
       init is idempotent (it guards on data-tc-init), so the re-run is free.
       Re-registering a type whose roots are ALREADY initialised is a silent
       no-op on those roots: init skips them on data-tc-init, so the new init
       never runs, and their live[] entries still hold the previous handlers
       object, so a later destroy calls the previous destroy. Swap handlers with
       destroy(root) then register(type, next) if that ever matters. */
    register: function (type, handlers) {
      registry[type] = handlers || {};
      if (booted) { TCKit.init(document); }
    },

    init: function (scope) {
      each(rootsIn(scope || document), function (root) {
        if (root.dataset.tcInit === '1') { return; }
        var type = root.getAttribute('data-tc-section');
        var h = registry[type];
        if (!h || !h.init) { return; }
        root.dataset.tcInit = '1';
        /* Per-module error isolation. One module throwing must degrade to one
           dead band, never to a dead page: without this the forEach aborts, no
           later section on the page initialises, and because window.TCKit is
           already assigned the file's own re-execution guard blocks recovery.
           The root keeps data-tc-init so it is not retried in this pass;
           destroy clears it, so a customizer reload gets a clean attempt. */
        var state = null;
        try {
          state = h.init(root);
        } catch (err) {
          if (window.console && window.console.error) {
            window.console.error('TCKit: init failed for section type "' + type + '"', err);
          }
          return;
        }
        live.push({ root: root, handlers: h, state: state });
      });
    },

    destroy: function (scope) {
      each(rootsIn(scope || document), function (root) {
        for (var i = live.length - 1; i >= 0; i--) {
          if (live[i].root !== root) { continue; }
          if (live[i].handlers.destroy) { live[i].handlers.destroy(root, live[i].state); }
          live.splice(i, 1);
        }
        delete root.dataset.tcInit;
      });
    },

    reducedMotion: function () {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    },

    /* Polite announcement into the section's own live region. Sections that
       need one render <p class="tc-visually-hidden" data-tc-live role="status"
       aria-live="polite"></p>. Precedent: snippets/product-gallery-announcer.liquid. */
    announce: function (root, text) {
      var region = root && root.querySelector('[data-tc-live]');
      if (!region) { return; }
      region.textContent = '';
      window.setTimeout(function () { region.textContent = text; }, 60);
    },

    /* Horizontal rail controller. Its one consumer is tc-testimonials; band 2
       turned out to be a static grid in the approved mockup and does not use it
       (see the collection-carousel module below).

       Reads [data-tc-track], [data-tc-slide], [data-tc-prev], [data-tc-next],
       and optionally [data-tc-nav] (the wrapper the controls live in) and
       [data-tc-current] (a visible position number) from root.
       options: { itemLabel: String, positionLabel: String }. positionLabel is a
       translated template with [n] and [total] placeholders and is what the band
       should pass; itemLabel is the fallback that builds "Quote 3 of 6".
       Returns { goTo, next, prev, update, destroy }.

       NEVER LOOPS AND NEVER SELF-ADVANCES. There is no timer in this function,
       no autoplay option, and no wrap-around: goTo clamps to the ends and the
       arrows disable there. That is a spec requirement (section 5) rather than a
       default, so do not add a setting for it.

       WHAT "SCROLLABLE" DECIDES, AND WHY IT IS ONE TEST. A track with nowhere to
       scroll needs no arrows, needs no keyboard scroll region and has no position
       to report, so all three key on the same measurement rather than on a
       repeated media query. The mockup draws band 5's controls below 768px and
       hides them above it, but the reason they go away there is that three
       quotes fit across the grid, and keying on the fact also covers a merchant
       who configures two quotes into a rail that never overflows on a phone
       either. It also keeps the number 768 out of this file: the band's
       stylesheet owns it, and the two cannot drift apart because only one of
       them has it. */
    createRail: function (root, options) {
      var opts = options || {};
      var label = opts.itemLabel || 'Item';
      var posTpl = opts.positionLabel || '';
      var track = root.querySelector('[data-tc-track]');
      var slides = track ? track.querySelectorAll('[data-tc-slide]') : [];
      var prev = root.querySelector('[data-tc-prev]');
      var next = root.querySelector('[data-tc-next]');
      var nav = root.querySelector('[data-tc-nav]');
      var counter = root.querySelector('[data-tc-current]');
      var noop = function () {};
      if (!track || !slides.length) { return { goTo: noop, next: noop, prev: noop, update: noop, destroy: noop }; }

      /* WHAT THE SECTION SHIPPED, captured before this function touches any of
         it, so destroy puts back the CONSUMER's static markup rather than the
         one consumer this file happened to be written against. A band with a
         single item ships no tabindex at all, and a destroy that added one
         would leave a tab stop the static markup never had; the same argument
         applies to every other attribute here, which is why they are all read
         rather than assumed. tc-testimonials ships its arrows hidden, its nav
         hidden and its counter at 1, so for that consumer these captures and
         the old hard-coded values agree byte for byte. A future rail whose
         arrows are statically visible is the case that used to lose them
         permanently to one theme-editor unload (Task 13 review, finding 2). */
      function snapshot(btn) {
        if (!btn) { return null; }
        return { hidden: btn.getAttribute('hidden'), disabled: !!btn.disabled };
      }
      function restore(btn, snap) {
        if (!btn || !snap) { return; }
        btn.disabled = snap.disabled;
        if (snap.hidden === null) { btn.removeAttribute('hidden'); }
        else { btn.setAttribute('hidden', snap.hidden); }
      }
      var staticTabindex = track.getAttribute('tabindex');
      var staticPrev = snapshot(prev);
      var staticNext = snapshot(next);
      var staticNavHidden = nav ? nav.hidden : false;
      var staticCount = counter ? counter.textContent : null;

      /* A slide's resting scroll position, in the track's own coordinates.
         scroll-padding is subtracted, and that term is not cosmetic. A band that
         bleeds its rail past the page gutter carries padding-inline on the track
         (so the first slide still lines up with the gutter) and the matching
         scroll-padding-inline (so the snap positions line up with it too). The
         browser then rests slide 0 at scrollLeft 0. Without the term every
         target computed here is one gutter further right than where the browser
         puts it, so the first arrow press over-scrolls by a gutter and the snap
         drags it back, and currentIndex reads the seam between two slides.
         Reading it from the computed style rather than taking it as an option
         keeps one number in one place, the stylesheet. */
      /* READ ONCE, NOT ONCE PER SLIDE PER EVENT. A scroll event is the worst
         possible place to ask the engine for computed style, and currentIndex
         compares every slide, so an uncached read cost one getComputedStyle per
         slide plus one: measured 7.00 per scroll event on six quotes, 13 at the
         schema's max_blocks, 273 across one smooth drag. Nothing between two
         scroll events can move the number, because it comes from the band's own
         media query, so the cache is cleared where the media query can change
         its mind: on resize (onResize below) and on a re-init, which builds a
         new closure anyway. Task 13 review, finding 3. */
      var padCache = null;
      function padStart() {
        if (padCache !== null) { return padCache; }
        var cs = window.getComputedStyle(track);
        var v = parseFloat(cs.scrollPaddingInlineStart);
        if (isNaN(v)) { v = parseFloat(cs.scrollPaddingLeft); }
        padCache = isNaN(v) ? 0 : v;
        return padCache;
      }
      function targetFor(slide, pad) {
        return slide.offsetLeft - track.offsetLeft - (pad === undefined ? padStart() : pad);
      }

      function currentIndex() {
        var x = track.scrollLeft;
        var pad = padStart(); /* hoisted out of the comparison loop */
        var best = 0, bestDist = Infinity;
        each(slides, function (s, i) {
          var d = Math.abs(targetFor(s, pad) - x);
          if (d < bestDist) { bestDist = d; best = i; }
        });
        return best;
      }

      function scrollable() { return track.scrollWidth - track.clientWidth > 1; }

      /* Disabling the button a keyboard visitor is standing on drops focus onto
         the body, and the next Tab restarts from the top of the document. So the
         partner arrow takes the focus first. Called BEFORE the disabled flags are
         written, which is what lets it read the partner's previous state.
         WCAG 2.2 SC 2.4.3 and SC 3.2.2. */
      function rescueFocus(btn, willDisable) {
        if (!btn || !willDisable || btn.disabled) { return; }
        if (document.activeElement !== btn) { return; }
        var other = btn === prev ? next : prev;
        if (other && !other.disabled) { other.focus(); }
      }

      /* The same failure from the other direction, and the one path the arrow
         rescue above does not reach: HIDING a subtree that contains focus drops
         focus on the floor exactly the way disabling a focused button does. A
         phone rotating to landscape crosses the band's 768px step, the nav goes
         display:none under the visitor's own focus, document.activeElement
         becomes <body>, and the next Tab restarts from the top of the document.
         Reproduced at 390 -> 900 with focus on either arrow: focus=BODY,
         tabindex=null (Task 13 review, finding 1).

         The track takes the focus, for the reason the partner arrow does in
         rescueFocus: it is the band's own region, it carries the band's
         accessible name, and the visitor stays where they were reading rather
         than being sent to the top of the page. The tabindex is written BEFORE
         the focus call because a <ul> is not focusable without it, and the
         guard further down then keeps it, because that guard never takes the
         attribute off the element that currently holds focus.
         preventScroll, because moving focus is the whole intent here; scrolling
         the page as a side effect of a rotation is not. WCAG 2.2 SC 2.4.3. */
      function rescueFromNav() {
        if (!nav || nav.hidden || !nav.contains(document.activeElement)) { return; }
        track.setAttribute('tabindex', '0');
        track.focus({ preventScroll: true });
      }

      /* Every write below is guarded on the value actually changing. Not
         cosmetic: an unconditional write invalidates style, so on a drag each
         event's writes made the next event's reads more expensive, and a rail
         under a finger fires these dozens of times a second. Measured across
         one smooth drag of 39 scroll events: 195 attribute writes before, 7
         after, which is the five positions the counter really passes through
         plus the two ends really disabling. */
      function update() {
        var live = scrollable();
        var pad = padStart();
        var i = currentIndex();
        var atStart = track.scrollLeft <= targetFor(slides[0], pad) + 1;
        var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
        if (nav && !live) { rescueFromNav(); }
        if (nav && nav.hidden !== !live) { nav.hidden = !live; }
        /* The track is a focusable region only while it actually scrolls: that
           is the whole of SC 2.1.1 here, because content a pointer can drag to
           and a keyboard cannot reach is unreachable content. The band ships the
           attribute in its static markup, because with the kit absent the phone
           rail is the state where it matters most; this only takes it off where
           it would be a tab stop that does nothing. Never taken off the element
           that currently has focus. */
        if (live) {
          if (track.getAttribute('tabindex') !== '0') { track.setAttribute('tabindex', '0'); }
        } else if (track !== document.activeElement) {
          track.removeAttribute('tabindex');
        }
        if (counter) {
          var count = String(live ? i + 1 : 1);
          if (counter.textContent !== count) { counter.textContent = count; }
        }
        rescueFocus(prev, atStart);
        rescueFocus(next, atEnd);
        if (prev && prev.disabled !== atStart) { prev.disabled = atStart; }
        if (next && next.disabled !== atEnd) { next.disabled = atEnd; }
        return i;
      }

      function positionText(i) {
        if (posTpl) { return posTpl.split('[n]').join(String(i + 1)).split('[total]').join(String(slides.length)); }
        return label + ' ' + (i + 1) + ' of ' + slides.length;
      }

      function goTo(i) {
        var clamped = Math.max(0, Math.min(slides.length - 1, i));
        track.scrollTo({
          left: targetFor(slides[clamped]),
          behavior: TCKit.reducedMotion() ? 'auto' : 'smooth'
        });
        TCKit.announce(root, positionText(clamped));
      }

      function onPrev() { goTo(currentIndex() - 1); }
      function onNext() { goTo(currentIndex() + 1); }

      /* A resize is the one input that can change the scroll-padding, because
         the band's own media query is what writes it, so it is also the one
         place the cached read has to be thrown away. */
      function onResize() { padCache = null; update(); }

      if (prev) { prev.removeAttribute('hidden'); prev.addEventListener('click', onPrev); }
      if (next) { next.removeAttribute('hidden'); next.addEventListener('click', onNext); }
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', onResize);
      update();

      return {
        goTo: goTo,
        next: onNext,
        prev: onPrev,
        update: update,
        /* Listeners off, then the static markup back, which is the same contract
           the accordion and tabs modules keep: destroy also clears
           data-tc-init, so whatever the section's pre-init CSS expects has to be
           what the DOM says afterwards. Every value put back here was READ from
           the consumer's markup at init rather than written from this file's
           idea of a rail, so a band that ships its arrows visible gets them back
           visible. For tc-testimonials that is arrows hidden, nav hidden,
           counter at 1 and the track focusable, unchanged. */
        destroy: function () {
          if (prev) { prev.removeEventListener('click', onPrev); }
          if (next) { next.removeEventListener('click', onNext); }
          track.removeEventListener('scroll', update);
          window.removeEventListener('resize', onResize);
          restore(prev, staticPrev);
          restore(next, staticNext);
          if (nav) { nav.hidden = staticNavHidden; }
          if (counter && staticCount !== null) { counter.textContent = staticCount; }
          if (staticTabindex === null) { track.removeAttribute('tabindex'); }
          else { track.setAttribute('tabindex', staticTabindex); }
        }
      };
    }
  };

  /* This assignment is also the cancel for the .tc-js expiry in
     snippets/tc-kit-assets.liquid (Task 10 review finding 1). That inline script
     removes the class if window.TCKit has not appeared within 3000ms, so that a
     page whose kit never loads falls back to the static layouts instead of
     keeping rules that promise an initialisation which is not coming.

     Two constraints follow and both are load-bearing:
       1. Keep this line ABOVE the module registrations and above boot(). It is
          what makes "the asset executed" the test rather than "every module
          initialised cleanly", which is the correct reading: per-module error
          isolation already handles a module that throws, and that band still
          gets its data-tc-init.
       2. Do not rename the global without changing the snippet's check. */
  /* Third leg of that contract (Task 10 re-review NEW-1): a kit that executes
     AFTER the expiry fired must put .tc-js back, or sections keyed on
     html:not(.tc-js) (tc-marquee's static wrap) keep their static layout while
     the kit animates it. Restore first, then cancel. */
  document.documentElement.classList.add('tc-js');
  window.TCKit = TCKit;

  /* Customizer lifecycle (spec section 5): re-init on section load, clean up on
     unload, bring the selected block into view on block select.
     These three listeners are attached HERE, above both the module block and
     the boot block, on purpose. The script is deferred, so document.readyState
     is already 'interactive' and boot() runs synchronously; anything attached
     below it would never be attached at all if a module registration or a
     module init threw. Keep them first. Modules and boot go below. */
  document.addEventListener('shopify:section:load', function (e) { TCKit.init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { TCKit.destroy(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    var root = e.target.closest ? e.target.closest('[data-tc-section]') : null;
    if (!root) { return; }
    var h = registry[root.getAttribute('data-tc-section')];
    if (h && h.blockSelect) { h.blockSelect(root, e.target); }
  });

  /* =========================================================================
     Section behavior modules.
     Tasks 10 to 13 append the collection-carousel, tabbed-collections,
     testimonials and accordion-band modules HERE, above the boot block, so
     they stay inside this IIFE and off the global scope.
     ========================================================================= */

  /* marquee: the pause/play toggle, and nothing else. Motion itself is pure CSS
     in sections/tc-marquee.liquid.

     WCAG 2.2 SC 2.2.2 requires a mechanism to pause anything that moves on its
     own for more than five seconds. The marquee scrolls for its whole loop
     duration, forever. Hover-pause is not a mechanism on a touch screen, and
     prefers-reduced-motion is a device preference rather than a page control,
     so the band needs a real control. Plan amendment (orchestrator, 2026-08-07,
     from the Task 3 review, finding 4).

     The button is BUILT HERE rather than rendered in the section, so the static
     markup stays complete and honest with JavaScript disabled: no dead control
     that cannot do anything. This is the kit's one permitted exception to "JS
     adds behavior, never content", and it is the exception because the content
     it adds is a control whose only purpose is behavior.

     The accessible name is fixed and the state rides on aria-pressed, which is
     the toggle-button pattern. Swapping the name as well would announce
     "Play ... pressed" and read as a contradiction.

     LOCALIZATION. The name is read from data-tc-pause-label on the section root
     and only falls back to the English literal below when that attribute is
     absent, so this module ships no untranslatable string of its own. Task 7's
     sections/tc-marquee.liquid emits the attribute through the theme's
     translation filter,
       data-tc-pause-label="{{ 'sections.marquee.pause_label' | t }}",
     with the key added to locales/en.default.json (and the parked es/fr files
     when they are unparked). The fallback exists only so the module still has a
     usable accessible name if a section forgets the attribute; it is not the
     shipping path.

     The icon markup below is a static string constant with no interpolation.
     The only variable text, the accessible name, is set with textContent. */
  var MARQUEE_ICONS =
    '<svg class="tc-icon-pause" width="13" height="15" viewBox="0 0 14 16" aria-hidden="true" focusable="false">' +
      '<rect x="1" y="1" width="3.5" height="14" fill="currentColor"></rect>' +
      '<rect x="9.5" y="1" width="3.5" height="14" fill="currentColor"></rect>' +
    '</svg>' +
    '<svg class="tc-icon-play" width="13" height="15" viewBox="0 0 14 16" aria-hidden="true" focusable="false">' +
      '<path d="M2 1l11 7-11 7z" fill="currentColor"></path>' +
    '</svg>';

  TCKit.register('marquee', {
    init: function (root) {
      if (!root.querySelector('.tc-marquee__track')) { return null; }

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'tc-marquee__toggle';
      button.setAttribute('aria-pressed', 'false');
      button.innerHTML = MARQUEE_ICONS;

      var name = document.createElement('span');
      name.className = 'tc-visually-hidden';
      name.textContent = root.getAttribute('data-tc-pause-label') || 'Pause the scrolling text';
      button.insertBefore(name, button.firstChild);

      /* is-playing is set only by an explicit press, and it is what beats the
         section's convenience hover-pause rule. Without it, pressing Play with
         the pointer resting on the band leaves the words frozen while the
         button reports "playing". The specificity that makes it win is
         documented in tc-kit.css. */
      function onClick() {
        var nowPaused = button.getAttribute('aria-pressed') !== 'true';
        button.setAttribute('aria-pressed', nowPaused ? 'true' : 'false');
        if (nowPaused) {
          root.classList.remove('is-playing');
          root.classList.add('is-paused');
        } else {
          root.classList.remove('is-paused');
          root.classList.add('is-playing');
        }
      }

      button.addEventListener('click', onClick);
      root.appendChild(button);
      return { button: button, onClick: onClick };
    },

    destroy: function (root, state) {
      root.classList.remove('is-paused');
      root.classList.remove('is-playing');
      if (!state || !state.button) { return; }
      state.button.removeEventListener('click', state.onClick);
      if (state.button.parentNode) { state.button.parentNode.removeChild(state.button); }
    }
  });

  /* accordion-band: exclusivity, and nothing else. The disclosure itself is the
     browser's: sections/tc-accordion-band.liquid ships native details/summary,
     so opening, closing, keyboard operation (Enter and Space on the summary) and
     the exposed expanded state all work with this file absent.

     WHY EXCLUSIVITY IS HERE RATHER THAN IN THE MARKUP. The HTML name attribute
     turns a group of details elements into a native exclusive accordion, and it
     would be the obvious answer. It cannot be used: a native exclusive accordion
     permits at most one open item, so the section could not ship every item open,
     and the JavaScript-off state would be one answer readable and the rest shut
     behind controls that still work but that nobody told the visitor to press.
     Global Constraints require the full content without JavaScript, so the
     exclusivity is the enhancement and the open state is the baseline. Do not
     "simplify" this by adding name to the section.

     This module writes no aria-expanded, and the section markup has none. The
     state is the browser's own, which is what removes the PR #50 desync failure
     mode by construction; a second copy that this module had to keep in step
     would put the failure straight back. The sitewide assets/tc-details-expanded.js
     does copy the native state onto each summary as aria-expanded on every toggle,
     because NVDA with Chrome does not hear the native change (issue #613). It only
     ever copies the browser's state, so there is still one source. Do not add
     aria-expanded handling here.

     The toggle event does not bubble, so the listener goes on each details
     element. Setting other.open = false inside the handler queues further toggle
     events; they land with open false and return on the first line. */
  TCKit.register('accordion-band', {
    init: function (root) {
      var items = root.querySelectorAll('[data-tc-item]');
      if (!items.length) { return null; }
      var openFirst = root.getAttribute('data-tc-open-first') === 'true';

      function onToggle(e) {
        if (!e.target.open) { return; }
        each(items, function (other) { if (other !== e.target) { other.open = false; } });
      }

      each(items, function (d, i) {
        d.open = openFirst && i === 0;
        d.addEventListener('toggle', onToggle);
      });

      return { items: items, onToggle: onToggle };
    },

    /* Listeners off first, then the static state back on. destroy also clears
       data-tc-init, which re-arms the section's pre-init CSS, and that CSS
       expects exactly this markup state: every item open, the stylesheet hiding
       all but the first. Restoring it keeps the DOM and the stylesheet telling
       the same story if the node outlives the unload. */
    destroy: function (root, state) {
      if (!state) { return; }
      each(state.items, function (d) {
        d.removeEventListener('toggle', state.onToggle);
        d.open = true;
      });
    },

    /* Clicking an item block in the theme editor opens that item and brings it
       into view. Opening it fires toggle, so exclusivity closes the others
       through the same path a visitor's click takes. */
    blockSelect: function (root, blockEl) {
      var d = blockEl.hasAttribute('data-tc-item') ? blockEl : blockEl.querySelector('[data-tc-item]');
      if (!d) { return; }
      d.open = true;
      d.scrollIntoView({ block: 'nearest', behavior: TCKit.reducedMotion() ? 'auto' : 'smooth' });
    }
  });

  /* tabbed-collections: the ARIA, the selection and the keyboard. The band's
     content, its layout and both of its presentations are already complete in
     sections/tc-tabbed-collections.liquid and assets/tc-kit.css: with this file
     absent the band is three h3-titled panels, all visible, with in-page anchors
     that jump to them. Nothing here adds content; it adds the pattern.

     EVERY TAB IS A TAB STOP, AND THE PANEL IS NOT ONE. THIS IS A DELIBERATE
     DEVIATION FROM THE APG TABS PATTERN; DO NOT "FIX" IT BACK TO ROVING
     TABINDEX. Issue #384, owner decision by Kevin on 2026-09-25, after PR #381
     shipped. The APG pattern gives a tablist one Tab stop (the selected tab,
     the others at tabindex -1 and reached only by the arrow keys) and makes the
     tabpanel itself a Tab stop. Kevin keyboard-walked the live band and Tab
     went selected tab, then the panel box, then the CTA: there was no way to
     reach Week-Long Escape or Extended Journey without knowing to press an
     arrow key, which real visitors do not discover. The decision is that Tab
     reachability wins over the APG idiom, so:

       - every tab carries tabindex 0, so Tab visits each trip in visual order;
       - Tab moves focus only. Landing on a tab does not select it; Enter,
         Space or a click activates it, exactly once, with focus kept;
       - the arrow keys, Home and End still move focus AND selection, as a
         parallel path for visitors who know the tabs idiom;
       - roles, aria-selected, aria-controls and the panel's aria-labelledby
         are unchanged, so a screen reader still announces a tab strip;
       - the panel carries no tabindex. It was a non-interactive stop drawn as
         a large outlined box, and the open panel's CTA link is the next Tab
         stop after the last tab, so nothing in it becomes unreachable.

     The disclosure presentation below 900px already gives every toggle its own
     Tab stop and is untouched by this.

     TWO CONTROL SETS, ONE COPY OF THE CONTENT, AND WHY THE MODULE OWNS THE
     SWITCH. The approved mockup draws a tab strip from 900px and an accordion
     below it, as two separate sets of controls. The section renders both, in
     true DOM order, and each is display: none in the other presentation, which
     removes it from the tab order and from the accessibility tree entirely. The
     panels are rendered once. So the module never moves anything: it decides
     which set is live and wires the panels' references to it.

       >= 900px  the strip is drawn. tablist / tab / tabpanel, every tab a Tab
                 stop (#384, above), arrows, Home, End, and each panel is
                 labelled by its TAB.
       <  900px  the toggles are drawn. aria-expanded and aria-controls on the
                 toggle, and each panel is a region labelled by its TOGGLE.

     The panel's role and name have to follow the drawing, which is the mockup's
     own annotated decision and Task 11 review finding 3: the tablist is not
     drawn at phone widths, so a panel still labelled by a tab button would have
     no accessible name at all. region + aria-labelledby is what gives the phone
     panel a name and a boundary a screen reader can land on.

     None of this can be written in Liquid. A role="tab" whose tablist is not
     drawn is a pattern with a missing part, and a phone accordion announced as a
     tab strip is a different lie, so the switch lives here, keyed on the same
     900px the stylesheet uses. The two numbers have to stay equal.

     One asymmetry between the halves is deliberate: a tablist with nothing
     selected is not a state the pattern has, so the tabs half always selects a
     trip, while the accordion half honours the section's "open the first tab"
     setting and can sit with everything closed. The section's pre-init CSS
     mirrors both, so nothing shifts when this file runs.

     SELECTION FOLLOWS ARROW FOCUS in the tabs half. That is the
     automatic-activation variant of the APG pattern, and it is the right one
     here because a panel is already in the document: moving to a tab costs a
     paint, not a fetch. It applies to the arrow keys, Home and End only. Focus
     that arrives by Tab selects nothing (#384): a visitor tabbing through the
     strip toward the CTA should not have the panel swap under them at every
     stop.

     Down and Up are mapped alongside Right and Left. The APG's horizontal
     tablist reserves them, but this strip becomes a vertical accordion 1px
     lower and a visitor who has just been arrowing down a page does not know
     which one they are on.

     SPACE OPERATES THE TOGGLE alongside Enter. Both control sets are anchors, so
     that the band still works as in-page navigation with the kit absent, and a
     browser activates an anchor on Enter only. A control that announces an
     expanded state is one an assistive-technology user reaches for Space on, so
     the disclosure half handles the key here rather than inheriting a link's
     behaviour. Task 11 review finding 2. */
  TCKit.register('tabbed-collections', {
    init: function (root) {
      var list = root.querySelector('[data-tc-tablist]');
      var tabs = root.querySelectorAll('[data-tc-tab]');
      var toggles = root.querySelectorAll('[data-tc-toggle]');
      var panels = root.querySelectorAll('[data-tc-panel]');
      if (!list || !panels.length) { return null; }
      if (tabs.length !== panels.length || toggles.length !== panels.length) { return null; }

      var mq = window.matchMedia('(min-width: 900px)');
      /* -1 means "no trip open", which only the accordion half can hold. */
      var current = root.getAttribute('data-tc-open-first') === 'true' ? 0 : -1;

      function indexIn(collection, el) {
        for (var i = 0; i < collection.length; i++) { if (collection[i] === el) { return i; } }
        return -1;
      }

      function paint() {
        var asTabs = mq.matches;
        if (asTabs && current < 0) { current = 0; }

        /* The strip carries its roles unconditionally. It is display: none below
           900, so there is nothing to strip and nothing left claiming a role it
           is not: that was the stale-attribute problem the single-set build had
           (Task 11 review finding 5) and two sets remove it rather than patch
           it. The name comes from the section: aria-labelledby when the band has
           a heading, and otherwise data-tc-label, which the Liquid fills from a
           locale key. */
        list.setAttribute('role', 'tablist');
        if (!list.getAttribute('aria-labelledby')) {
          var listName = root.getAttribute('data-tc-label');
          if (listName) { list.setAttribute('aria-label', listName); }
        }

        each(tabs, function (t, i) {
          t.setAttribute('role', 'tab');
          t.setAttribute('aria-controls', panels[i].id);
          t.setAttribute('aria-selected', i === current ? 'true' : 'false');
          /* Every tab is a Tab stop, selected or not: no roving -1. #384. */
          t.setAttribute('tabindex', '0');
        });

        each(toggles, function (a, i) {
          a.setAttribute('aria-controls', panels[i].id);
          a.setAttribute('aria-expanded', i === current ? 'true' : 'false');
        });

        /* No tabindex on a panel in either presentation. The tabpanel used to
           carry tabindex 0 per the APG; #384 removed that Tab stop (see the
           module head). Nothing in the kit focuses a panel: the only focus()
           calls in this module go to a tab. */
        each(panels, function (p, i) {
          if (asTabs) {
            p.setAttribute('role', 'tabpanel');
            p.setAttribute('aria-labelledby', tabs[i].id);
          } else {
            p.setAttribute('role', 'region');
            p.setAttribute('aria-labelledby', toggles[i].id);
          }
          p.removeAttribute('tabindex');
          p.hidden = i !== current;
        });
      }

      function select(i, focusTab) {
        current = i;
        paint();
        if (focusTab && current >= 0) { tabs[current].focus(); }
      }

      /* Both control sets are anchors pointing at their own panel, so the
         default action is a jump the pattern does not want. Focus is left to the
         browser: a pointer press already lands on the anchor, and calling
         focus() here would scroll the page under a visitor who only clicked.

         BOTH CLICK HANDLERS RUN IN THE CAPTURE PHASE AND STOP THE EVENT (#377).
         theme.js bindInPageLinks() puts a bubble-phase click listener on every
         a[href^="#"] that calls forceFocus() on the target without checking
         defaultPrevented, so a click or Enter on a tab threw focus into the
         panel, scrolled the page and killed the arrow keys. At the target,
         capture listeners run before bubble listeners whatever the order they
         were added in, so capture plus stopImmediatePropagation keeps that
         listener from ever seeing a tab click. The toggles get the same
         treatment. Since #384 no panel carries a tabindex at either width, so
         the helper's focus() on a panel is a no-op today anyway, but the fix
         must not depend on that: the listener would start stealing focus
         again the moment anything made a panel focusable. */
      function onTabClick(e) {
        var i = indexIn(tabs, e.currentTarget);
        if (i < 0) { return; }
        e.preventDefault();
        e.stopImmediatePropagation();
        select(i, false);
      }

      /* The disclosure half is a toggle: clicking the open trip closes it. */
      function onToggleClick(e) {
        var i = indexIn(toggles, e.currentTarget);
        if (i < 0) { return; }
        e.preventDefault();
        e.stopImmediatePropagation();
        select(i === current ? -1 : i, false);
      }

      /* Space activates a tab as well as Enter, which the APG tabs pattern
         requires and the Dentons audit's item 14 asks of every control in the
         kit. The tabs are anchors, and a browser turns Enter on an anchor into a
         click (onTabClick above) but does nothing with Space except scroll the
         page, so Space is handled here and its default prevented. Enter is left
         alone for the same reason the disclosure half leaves it: handling it
         here as well would run the selection twice. Since #384 every tab is a
         Tab stop and Tab focus selects nothing, so Space (or Enter) on a tab
         reached by Tab is the ordinary way to open it; after an arrow key it
         re-selects the tab that is already selected, and the scroll it stops
         matters every time. Task 17 finding F2. */
      function onTabKeydown(e) {
        if (!mq.matches) { return; }
        var i = indexIn(tabs, e.currentTarget);
        if (i < 0) { return; }
        if (e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          select(i, false);
          return;
        }
        var next = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { next = (i + 1) % tabs.length; }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { next = (i - 1 + tabs.length) % tabs.length; }
        if (e.key === 'Home') { next = 0; }
        if (e.key === 'End') { next = tabs.length - 1; }
        if (next === null) { return; }
        e.preventDefault();
        select(next, true);
      }

      /* Space, and only Space. Enter already activates an anchor and arrives as
         a click, so intercepting it here would run the toggle twice. ' ' is the
         modern key value and 'Spacebar' is the pre-Edge-79 spelling; preventing
         the default is what stops the page scrolling under the visitor. */
      function onToggleKeydown(e) {
        if (e.key !== ' ' && e.key !== 'Spacebar') { return; }
        var i = indexIn(toggles, e.currentTarget);
        if (i < 0) { return; }
        e.preventDefault();
        select(i === current ? -1 : i, false);
      }

      function onMediaChange() { paint(); }

      /* Capture (the true) on both clicks: see onTabClick. destroy has to pass
         the same flag or removeEventListener misses them. */
      each(tabs, function (t) {
        t.addEventListener('click', onTabClick, true);
        t.addEventListener('keydown', onTabKeydown);
      });
      each(toggles, function (a) {
        a.addEventListener('click', onToggleClick, true);
        a.addEventListener('keydown', onToggleKeydown);
      });
      /* addListener is the pre-Safari-14 spelling and costs two lines to keep. */
      if (mq.addEventListener) { mq.addEventListener('change', onMediaChange); }
      else if (mq.addListener) { mq.addListener(onMediaChange); }

      paint();
      return {
        list: list, tabs: tabs, toggles: toggles, panels: panels, mq: mq,
        onTabClick: onTabClick, onTabKeydown: onTabKeydown,
        onToggleClick: onToggleClick, onToggleKeydown: onToggleKeydown,
        onMediaChange: onMediaChange
      };
    },

    /* Listeners off, then the static markup back. destroy also clears
       data-tc-init, which re-arms the section's pre-init CSS, and that CSS
       expects exactly this state: no roles, no hidden attributes, every panel
       present. Restoring it keeps the DOM and the stylesheet telling the same
       story if the node outlives the unload. */
    destroy: function (root, state) {
      if (!state) { return; }
      each(state.tabs, function (t) {
        t.removeEventListener('click', state.onTabClick, true);
        t.removeEventListener('keydown', state.onTabKeydown);
        t.removeAttribute('role');
        t.removeAttribute('aria-selected');
        t.removeAttribute('aria-controls');
        t.removeAttribute('tabindex');
      });
      each(state.toggles, function (a) {
        a.removeEventListener('click', state.onToggleClick, true);
        a.removeEventListener('keydown', state.onToggleKeydown);
        a.removeAttribute('aria-expanded');
        a.removeAttribute('aria-controls');
      });
      each(state.panels, function (p) {
        p.hidden = false;
        p.removeAttribute('role');
        p.removeAttribute('aria-labelledby');
        p.removeAttribute('tabindex');
      });
      state.list.removeAttribute('role');
      state.list.removeAttribute('aria-label');
      if (state.mq.removeEventListener) { state.mq.removeEventListener('change', state.onMediaChange); }
      else if (state.mq.removeListener) { state.mq.removeListener(state.onMediaChange); }
    },

    /* Clicking a tab block in the theme editor opens that trip and brings it
       into view. Driven through the drawn control's own click so the editor
       takes the same path a visitor does, and only when the trip is not already
       open: in the accordion half a click on the open trip closes it, which is
       right for a visitor and wrong for a merchant who just selected the block.
       aria-expanded is maintained on the toggles in BOTH presentations, so it is
       the one attribute that answers "is this trip open" at either width. */
    blockSelect: function (root, blockEl) {
      var panel = blockEl.hasAttribute('data-tc-panel') ? blockEl : null;
      if (!panel && blockEl.closest) { panel = blockEl.closest('[data-tc-panel]'); }
      if (!panel && blockEl.querySelector) { panel = blockEl.querySelector('[data-tc-panel]'); }
      if (!panel) { return; }
      var panels = root.querySelectorAll('[data-tc-panel]');
      var tabs = root.querySelectorAll('[data-tc-tab]');
      var toggles = root.querySelectorAll('[data-tc-toggle]');
      var wide = !!(window.matchMedia && window.matchMedia('(min-width: 900px)').matches);
      for (var i = 0; i < panels.length; i++) {
        if (panels[i] !== panel) { continue; }
        var control = wide ? tabs[i] : toggles[i];
        var open = toggles[i] && toggles[i].getAttribute('aria-expanded') === 'true';
        if (control && !open) { control.click(); }
        panel.scrollIntoView({ block: 'nearest', behavior: TCKit.reducedMotion() ? 'auto' : 'smooth' });
        return;
      }
    }
  });

  /* collection-carousel: the theme editor, and nothing else.

     THIS MODULE HAS NO init AND THAT IS THE POINT. Band 2 of the approved mockup
     is a static tile grid at every width, not a rail: no overflow container, no
     scroll snap, no arrows, no position announcement. The task brief described a
     desktop rail driven by createRail, but it predates the revision 2 pass Kevin
     approved at the Task 4 gate, and the mockup is binding. So there is no
     behaviour to attach on load, and sections/tc-collection-carousel.liquid
     ships a band that is complete before this file executes.

     Registering a handler object with no init is a supported shape rather than a
     trick, and the two halves of the kit read it differently on purpose:
     TCKit.init returns early on any root whose handlers have no init, BEFORE it
     stamps data-tc-init, so this section's root is never stamped, never enters
     live[], and never needs a destroy. The shopify:block:select listener reads
     the registry directly and so still finds this handler. The result is a band
     with zero runtime footprint on a visitor's page and a working editor.

     WHY THE EDITOR STILL NEEDS SOMETHING. Selecting a block scrolls the SECTION
     into view, not the block. At a phone width four staggered 4:5 tiles are
     roughly two screens tall, so a merchant clicking the fourth tile in the
     sidebar is looking at the first two. Bringing the selected tile into view is
     the whole of the behaviour, and 'nearest' is what keeps it from scrolling a
     tile that is already visible.

     TCKit.createRail is not orphaned by any of this: band 7 (tc-testimonials) is
     a rail on the phone in the same mockup and is the helper's real consumer. */
  TCKit.register('collection-carousel', {
    blockSelect: function (root, blockEl) {
      var cell = blockEl.classList && blockEl.classList.contains('tc-collections__cell')
        ? blockEl
        : (blockEl.closest ? blockEl.closest('.tc-collections__cell') : null);
      if (!cell) { return; }
      cell.scrollIntoView({ block: 'nearest', behavior: TCKit.reducedMotion() ? 'auto' : 'smooth' });
    }
  });

  /* testimonials: the rail controls, and nothing else.

     sections/tc-testimonials.liquid ships all six quotes in a scroll-snap row
     that a finger drags and a keyboard scrolls, and from 768px the same markup
     is a three-across grid with nothing to page through. Both of those are CSS.
     What this module adds is the pair of arrows, the visible position count and
     the polite announcement, and it adds them by REVEALING controls the section
     ships hidden, so a page without this file carries no dead buttons and no
     counter frozen at 1 beside a rail the visitor has already swiped.

     NO AUTOPLAY, NO LOOP, AND NO SETTING FOR EITHER. There is no timer here and
     none in createRail. Spec section 5, and the mockup's own annotation on band
     5 says the same thing in one line.

     The strings come from the section, which fills them through the theme's
     translation filter, so this module ships no untranslatable text of its own.
     The English fallbacks exist for a section that forgets the attribute. Same
     rule and same precedent as the marquee's pause label. */
  TCKit.register('testimonials', {
    init: function (root) {
      return {
        rail: TCKit.createRail(root, {
          itemLabel: root.getAttribute('data-tc-item-label') || 'Quote',
          positionLabel: root.getAttribute('data-tc-position-label') || ''
        })
      };
    },

    destroy: function (root, state) {
      if (state && state.rail) { state.rail.destroy(); }
    },

    /* Selecting a quote block in the theme editor brings it into view. On a
       phone the rail is scrolled horizontally and the fourth quote is three
       screens to the right, so without this a merchant clicking it in the
       sidebar is looking at the first one. inline: 'nearest' is what stops a
       quote that is already on screen from being dragged to the edge. */
    blockSelect: function (root, blockEl) {
      var slide = blockEl.hasAttribute('data-tc-slide') ? blockEl : (blockEl.closest ? blockEl.closest('[data-tc-slide]') : null);
      if (!slide) { return; }
      slide.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: TCKit.reducedMotion() ? 'auto' : 'smooth' });
    }
  });

  function boot() { booted = true; TCKit.init(document); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
